/**
 * Wrapper around the `<building-viewer>` custom element from the prototype.
 *
 * `public/building-viewer.js` is the prototype's file unchanged. It defines the
 * element, waits for `window.THREE` (loaded from the CDN in index.html) and draws
 * the exploded six-storey model. Loading it once here keeps the script out of the
 * initial HTML for every route that does not use it.
 */
import { useEffect, useState, type CSSProperties } from 'react'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'building-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
    }
  }
}

const SRC = '/building-viewer.js'
let loading: Promise<void> | null = null

function load(): Promise<void> {
  if (loading) return loading
  loading = new Promise<void>((resolve, reject) => {
    if (customElements.get('building-viewer')) return resolve()
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('building-viewer failed to load')))
      return
    }
    const el = document.createElement('script')
    el.src = SRC
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('building-viewer failed to load'))
    document.head.appendChild(el)
  })
  return loading
}

export function BuildingViewer({ style }: { style?: CSSProperties }) {
  const [ready, setReady] = useState(() => Boolean(customElements.get('building-viewer')))

  useEffect(() => {
    let live = true
    load().then(
      () => live && setReady(true),
      () => {
        /* the hero still reads fine without the model */
      },
    )
    return () => {
      live = false
    }
  }, [])

  if (!ready) return <div style={style} aria-hidden="true" />
  return <building-viewer style={style} />
}
